import { Component, Input, OnInit } from "@angular/core";
import * as Highcharts from "highcharts";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { ConsensusSummaryDataService } from "../../consensus-summary-data.service";
import { catchError } from "rxjs/operators";
import { throwError, Subscription } from "rxjs";
import { RatingConsensus } from "../../interfaces";

@Component({
  selector: "app-rating-consensus",
  templateUrl: "./rating-consensus.component.html",
  styleUrls: ["./rating-consensus.component.scss"],
})
export class RatingConsensusComponent implements OnInit {
  @Input() primaryTicker = "";
  @Input() brokerInRatingConsensus: number;
  @Input() totalBroker: number;
  //subscribers
  tprRatingConensusSubsciber: Subscription;

  constructor(private dataService: ConsensusSummaryDataService) {}

  hasConsensus = false;
  ratingConsensusDataReady = false;
  data: RatingConsensus = {};
  highcharts = Highcharts;
  chartOptions: any;
  faInfoCircle = faInfoCircle;
  dialOneConfig: any = {
    radius: "75%",
    backgroundColor: "black",
    baseWidth: 6,
    baseLength: "0%",
    rearLength: 0,
  };

  dialTwoConfig: any = {
    radius: "75%",
    backgroundColor: "gray",
    baseWidth: 4,
    baseLength: "0%",
    rearLength: 1,
  };

  states: {
    hover: {
      enabled: false;
    };
  };

  dataLabels: any = {
    formatter: function () {
      return null;
    },
  };

  setChartOptions = (series: any): any => {
    const chart = {
      type: "gauge",
      plotBackgroundColor: null,
      plotBackgroundImage: null,
      plotBorderWidth: 0,
      margin: [0, 0, 0, 0],
      spacingTop: 0,
      spacingBottom: 0,
      spacingLeft: 0,
      spacingRight: 0,
      plotShadow: false,
      height: 240,
    };

    const pane = {
      startAngle: -90,
      endAngle: 89.9,
      background: null,
      center: ["48%", "65%"],
      size: "88%",
    };

    const color = {
      linearGradient: { x1: "0%", x2: "100%", y1: "0%", y2: "0%" },
      stops: this.hasConsensus
        ? [
            ["0%", "#B40000"],
            ["43%", "#FFC846"],
            ["56%", "#FFC846"],
            ["100%", "#006800"],
          ]
        : [
            ["0%", "#E9EBEE"],
            ["100%", "#E9EBEE"],
          ],
    };

    const labels = {
      distance: 20,
      style: {
        fontSize: "14px",
        color: this.hasConsensus ? "black" : "#E9EBEE",
      },
      formatter: function () {
        switch (this.value) {
          case 1:
            return '<span style="font-weight: bold;">Sell</span> 1 &nbsp;&nbsp;';
          case 2:
            return "2";
          case 3:
            return '<span style="font-weight: bold;">Hold</span><br>3';
          case 4:
            return "4";
          case 5:
            return '&nbsp;&nbsp; 5 <span style="font-weight: bold;">Buy</span>';
          default:
            return this.value;
        }
      },
    };

    const potband = [
      {
        from: 1,
        to: 5,
        thickness: 15,
        color: color,
        minRange: 1,
        maxRange: 5,
        allowDecimals: false,
      },
    ];

    const chartOptions = {
      exporting: { enabled: false },
      credits: { enabled: false },
      title: { style: { display: "none" } },
      chart: chart,
      lang: { noData: "" },
      pane: pane,
      yAxis: {
        min: 1,
        max: 5,
        tickPosition: "inside",
        tickColor: "#FFFFFF",
        tickLength: 20,
        tickWidth: 0,
        minorTickInterval: 0.5,
        minortickPosition: "inside",
        minorTickWidth: 0,
        minorTickColor: "#FFFFFF",
        minorTickLength: 20,
        labels: labels,
        lineWidth: 0,
        plotBands: potband,
        allowDecimals: false,
        minRange: 1,
        maxRange: 5,
      },
      series: series,
      tooltip: {
        enabled: false,
      },
    };
    return chartOptions;
  };

  getRatingConsensus = (): void => {
    this.ratingConsensusDataReady = false;
    this.tprRatingConensusSubsciber = this.dataService
      .getRatingConsensus(this.primaryTicker)
      .pipe(
        catchError((err) => {
          return throwError(err);
        }),
      )
      .subscribe((response: any) => {
        if (response && response["status"] === "success") {
          const data = response["data"][this.primaryTicker];
          this.hasConsensus = data["has_current_consensus"];
          this.data = {
            rating: data["rating"],
            rating_numeric: data["rating_numeric"],
            you_rating: data["you_rating"],
            broker: data["broker"],
            normalized_rating: data["normalized_rating"],
          };
          if (this.data.you_rating) {
            this.data.you_rating = Number(this.data.you_rating.toFixed(2));
          }
          const series = this.setSeries();
          this.chartOptions = this.setChartOptions(series);
          this.ratingConsensusDataReady = true;
        }
      });
  };

  setSeries = () => {
    const series = [];
    if (this.data.rating_numeric && this.hasConsensus) {
      series.push({
        name: `Consensus Rating:${this.data.rating}`,
        data: [Number(this.data.rating_numeric)],
        dial: this.dialOneConfig,
        dataLabels: this.dataLabels,
        states: this.states,
      });
    } else {
      series.push({
        name: `Consensus Rating:${this.data.rating}`,
        dial: this.dialOneConfig,
        dataLabels: this.dataLabels,
        states: this.states,
        pivot: {
          backgroundColor: "white",
        },
      });
    }
    if (this.data.you_rating) {
      series.push({
        name: `${this.data.broker} : ${this.data.normalized_rating}`,
        data: [Number(this.data.you_rating)],
        dial: this.dialTwoConfig,
        dataLabels: this.dataLabels,
        pivot: {
          backgroundColor: "#16181C",
          radius: 6,
        },
        states: this.states,
      });
    }
    return series;
  };

  ngOnInit() {
    this.getRatingConsensus();
  }

  ngOnDestroy() {
    if (this.tprRatingConensusSubsciber) {
      this.tprRatingConensusSubsciber.unsubscribe();
    }
  }
}
